import React, { useEffect, useRef } from 'react';
import { reducedMotion } from './effects';

// Rust, drifting up off the hero.
//
// A canvas rather than DOM nodes: a hundred of these as elements would cost a
// hundred layout entries for something the reader is not meant to notice. It
// stops entirely when the hero leaves the screen, so scrolling the rest of the
// page costs nothing.
export default function Embers() {
  const canvas = useRef(null);

  useEffect(() => {
    const node = canvas.current;
    if (!node || reducedMotion()) return;
    const ctx = node.getContext('2d');
    let frame = 0;
    let visible = true;
    let width = 0;
    let height = 0;
    let sparks = [];

    const seed = (spark, fresh) => {
      spark.x = Math.random() * width;
      spark.y = fresh ? Math.random() * height : height + 10;
      spark.r = 0.6 + Math.random() * 1.6;
      spark.speed = 8 + Math.random() * 26;
      spark.drift = (Math.random() - 0.5) * 14;
      spark.life = 0;
      spark.span = 5 + Math.random() * 7;
      return spark;
    };

    const resize = () => {
      const box = node.getBoundingClientRect();
      const dpr = Math.min(2, devicePixelRatio || 1);
      width = box.width; height = box.height;
      node.width = Math.round(width * dpr);
      node.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Density by area, so a phone does not run a desktop's particle count.
      const count = Math.round(Math.min(90, (width * height) / 14000));
      sparks = Array.from({ length: count }, () => seed({}, true));
    };

    resize();
    const onResize = () => resize();
    addEventListener('resize', onResize);

    const watcher = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; }, { threshold: 0 });
    watcher.observe(node);

    let last = performance.now();
    const draw = (now) => {
      frame = requestAnimationFrame(draw);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!visible) return;
      ctx.clearRect(0, 0, width, height);
      for (const spark of sparks) {
        spark.life += dt;
        spark.y -= spark.speed * dt;
        spark.x += Math.sin(spark.life * 1.4) * spark.drift * dt;
        if (spark.y < -10 || spark.life > spark.span) seed(spark, false);
        // Fade in off the bottom and out at the top of its own life.
        const fade = Math.min(1, spark.life * 2) * Math.max(0, 1 - spark.life / spark.span);
        ctx.beginPath();
        ctx.fillStyle = `rgba(255, ${120 + Math.round(spark.r * 40)}, 69, ${(fade * 0.55).toFixed(3)})`;
        ctx.arc(spark.x, spark.y, spark.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };
    frame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frame);
      watcher.disconnect();
      removeEventListener('resize', onResize);
    };
  }, []);

  return <canvas className="ox-embers" ref={canvas} aria-hidden="true" />;
}
